/**
 * To join a private group, a site member must submit a Join Request, which can be approved or rejected by an admin.
 * When the request is approved, the site member becomes a group member.
 */
export interface JoinGroupRequest {
  /**
   * Member ID. See the Members API for more details.
   * @readonly
   * @format GUID
   */
  siteMemberId?: string;
  /**
   * Contact ID. See the Contacts API for more details.
   * @readonly
   * @format GUID
   */
  contactId?: string;
  /** Join group request status. */
  status?: RequestStatusWithLiterals;
  /** Join group request details. */
  requestDetails?: RequestDetails;
}

export enum RequestStatus {
  /** Pending group request. */
  PENDING = 'PENDING',
  /** Approved group request. */
  APPROVED = 'APPROVED',
  /** Rejected group request. */
  REJECTED = 'REJECTED',
  /** Canceled group request. */
  CANCELED = 'CANCELED',
}

/** @enumType */
export type RequestStatusWithLiterals =
  | RequestStatus
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELED';

export interface RequestDetails {
  /**
   * Reason the request has been rejected.
   * @maxLength 1000
   */
  rejectionReason?: string | null;
}

export interface SocialGroupsEvent extends SocialGroupsEventPayloadOneOf {
  memberJoined?: MemberJoinedGroup;
  membersAdded?: MembersAddedToGroup;
  joinRequestsApproved?: JoinRequestsApproved;
  membersInvited?: MembersInvitedToGroup;
}

/** @oneof */
export interface SocialGroupsEventPayloadOneOf {
  memberJoined?: MemberJoinedGroup;
  membersAdded?: MembersAddedToGroup;
  joinRequestsApproved?: JoinRequestsApproved;
  membersInvited?: MembersInvitedToGroup;
}

export interface MemberJoinedGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberId?: string;
}

export interface MembersAddedToGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  whoAddedId?: string | null;
  /** @format GUID */
  siteMemberIds?: string[];
  /**
   * Used for Apes sticky experiment
   * @format GUID
   */
  operationId?: string;
}

export interface JoinRequestsApproved {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberIds?: string[];
  /**
   * Used for Apes sticky experiment
   * @format GUID
   */
  operationId?: string;
}

export interface MembersInvitedToGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberIds?: string[];
}

export interface GetJoinRequirementsRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId?: string;
  /**
   * Answers to membership questions. They can be empty, but submit join group request will fail if an answer to a required question is omitted.
   * @maxSize 50
   */
  membershipQuestionAnswers?: MembershipQuestionAnswer[];
  autoInviteId?: string | null;
}

/** Answer to a membership question. */
export interface MembershipQuestionAnswer {
  /**
   * Question ID.
   * @format GUID
   */
  id?: string;
  /**
   * Answer text.
   * @maxLength 300
   */
  text?: string | null;
}

export interface GetJoinRequirementsResponse {
  violation?: Violation;
}

export interface PaidPlan {
  planId?: string;
  name?: string;
  startsAt?: Date | null;
}

export enum ViolationType {
  NONE = 'NONE',
  NOT_LOGGED_IN = 'NOT_LOGGED_IN',
  ALREADY_JOINED = 'ALREADY_JOINED',
  SECRET_GROUP = 'SECRET_GROUP',
  EVENTS = 'EVENTS',
  PRICING_PlANS = 'PRICING_PlANS',
  MEMBERSHIP_QUESTIONS = 'MEMBERSHIP_QUESTIONS',
  ADMIN_APPROVAL = 'ADMIN_APPROVAL',
}

/** @enumType */
export type ViolationTypeWithLiterals =
  | ViolationType
  | 'NONE'
  | 'NOT_LOGGED_IN'
  | 'ALREADY_JOINED'
  | 'SECRET_GROUP'
  | 'EVENTS'
  | 'PRICING_PlANS'
  | 'MEMBERSHIP_QUESTIONS'
  | 'ADMIN_APPROVAL';

export interface EventsViolationOptions {
  /** Events which allow user to join the group. */
  eventIds?: string[];
}

export interface PricingPlanViolationOptions {
  installed?: boolean;
  /** Plan ids which allow user to join the group. */
  requiredPlans?: PaidPlan[];
  /** Plan ids which user have, but they don't allow to join group right now, because they start some time in the future. */
  futurePlans?: PaidPlan[];
}

export interface MembershipQuestionViolationOptions {
  requiredQuestionIds?: string[];
}

export interface Violation extends ViolationViolationOptionsOneOf {
  eventsOptions?: EventsViolationOptions;
  pricingPlansOptions?: PricingPlanViolationOptions;
  membershipQuestionsOptions?: MembershipQuestionViolationOptions;
  violationType?: ViolationTypeWithLiterals;
}

/** @oneof */
export interface ViolationViolationOptionsOneOf {
  eventsOptions?: EventsViolationOptions;
  pricingPlansOptions?: PricingPlanViolationOptions;
  membershipQuestionsOptions?: MembershipQuestionViolationOptions;
}

export interface SubmitJoinGroupRequestRequest {
  /**
   * Relevant group.
   * @format GUID
   */
  groupId?: string;
  /**
   * Answers to membership questions. They can be empty, but submit join group request will fail if an answer to a required question is omitted.
   * @maxSize 50
   */
  membershipQuestionAnswers?: MembershipQuestionAnswer[];
}

export interface SubmitJoinGroupRequestResponse {
  /** Submitted join group request. */
  joinGroupRequest?: JoinGroupRequest;
}

export interface CancelJoinGroupRequestRequest {
  /**
   * Relevant group.
   * @format GUID
   */
  groupId?: string;
}

export interface CancelJoinGroupRequestResponse {
  /** Cancelled join group request. */
  joinGroupRequest?: JoinGroupRequest;
}

export interface JoinGroupRequestCancelled {
  /**
   * Group ID for which join request was cancelled.
   * @format GUID
   */
  groupId?: string;
  /** Cancelled join group request. */
  joinGroupRequest?: JoinGroupRequest;
}

export interface ApproveJoinGroupRequestsRequest {
  /**
   * ID of the group requested to join.
   * @format GUID
   */
  groupId: string;
  /**
   * IDs of the site members to approve.
   * @format GUID
   * @maxSize 100
   */
  memberIds?: string[];
}

export enum ItemsToUpdate {
  /** Take into account only items which are listed in the request. */
  BY_ID = 'BY_ID',
  /** Update all items. */
  ALL_ITEMS = 'ALL_ITEMS',
}

/** @enumType */
export type ItemsToUpdateWithLiterals = ItemsToUpdate | 'BY_ID' | 'ALL_ITEMS';

export interface ApproveJoinGroupRequestsResponse {
  /** Approved join group requests. */
  joinGroupRequests?: JoinGroupRequest[];
}

export interface JoinGroupRequestApproved {
  /**
   * Group ID for which join request was approved.
   * @format GUID
   */
  groupId?: string;
  /** Approved join group request. */
  joinGroupRequest?: JoinGroupRequest;
}

export interface RejectJoinGroupRequestsRequest {
  /**
   * ID of the group requested to join.
   * @format GUID
   */
  groupId: string;
  /**
   * Rejection info.
   * @maxSize 100
   */
  rejections?: Rejection[];
}

export interface Rejection {
  /** Member ID to reject. */
  memberId?: string;
  /**
   * Rejection reason. Free text that will be displayed to the rejected site member. Limited to 1,000 characters.
   * @maxLength 1000
   */
  reason?: string | null;
}

export interface RejectJoinGroupRequestsResponse {
  /** Rejected join group requests. */
  joinGroupRequests?: JoinGroupRequest[];
}

export interface JoinGroupRequestRejected {
  /**
   * Group ID for which join request was rejected.
   * @format GUID
   */
  groupId?: string;
  /** Rejected join group request. */
  joinGroupRequest?: JoinGroupRequest;
}

export interface ListJoinGroupRequestsRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Maximum number of join group requests to retrieve. Defaults to 100.
   * @max 100
   */
  limit?: number | null;
  /** Number of join group requests to skip in the list. */
  offset?: number | null;
}

export enum OwnershipFilter {
  /** All items. */
  ALL = 'ALL',
  /** Items for the current site member. */
  CURRENT_MEMBER = 'CURRENT_MEMBER',
}

/** @enumType */
export type OwnershipFilterWithLiterals =
  | OwnershipFilter
  | 'ALL'
  | 'CURRENT_MEMBER';

export interface ListJoinGroupRequestsResponse {
  /** Retrieved join group requests. */
  joinGroupRequests?: JoinGroupRequest[];
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface QueryJoinGroupRequestsRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  query?: Query;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryJoinGroupRequestsResponse {
  /** Join group requests. */
  joinGroupRequests?: JoinGroupRequest[];
  metadata?: PagingMetadata;
}

export interface RejectAllJoinGroupRequestsRequest {}

export interface RejectAllJoinGroupRequestsResponse {
  /** Rejected join group requests. */
  joinGroupRequests?: JoinGroupRequest[];
}

export interface ApproveAllJoinGroupRequestsRequest {}

export interface ApproveAllJoinGroupRequestsResponse {
  /** Rejected join group requests. */
  joinGroupRequests?: JoinGroupRequest[];
}

export interface ListAllJoinGroupRequestsRequest {
  query?: Query;
}

export interface ListAllJoinGroupRequestsResponse {
  /** Join group requests. */
  joinGroupRequests?: JoinGroupRequest[];
  metadata?: PagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
